import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import { MenuItem, Select, useMediaQuery, useTheme, Checkbox } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import config from "../../config";
import { getDeviceMonitorLatest } from "../device/deviceApi";
import { getOrganizationId } from "../manageData/reportsApi";
import {
  buildStopMonitorRows,
  buildVehicleMonitorRows,
  DEFAULT_SENSOR_RANGES,
  normalizeSensorType,
} from "../../utils/vehicleMonitorData";
import {
  extractLiveTelemetry,
  SENSOR_UNITS as LIVE_SENSOR_UNITS,
} from "../../pages/vehicle/vehicleTrack/liveTelemetry";
import DownloadDataButton from "../common/DownloadDataButton";
import { usePermission } from "../../hooks/usePermission";

// ─── Styled Components (from DynamicTable) ────────────────────────────────────

const ResponsiveTableContainer = styled(TableContainer)(() => ({
  marginTop: "20px",
  overflowX: "auto",
  overflowY: "auto",
  borderRadius: "10px",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  "&::-webkit-scrollbar": { width: "8px", height: "0px", borderRadius: "10px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#647490",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#B7BFCC",
    borderRadius: "10px",
    margin: "5px",
  },
  scrollbarWidth: "thin",
  scrollbarColor: "#647490 #B7BFCC",
}));

const ResponsiveTableCell = styled(TableCell)(({ theme }) => ({
  padding: "8px 12px",
  whiteSpace: "nowrap",
  color: "#1F242E",
  fontSize: "16px",
  borderRight: "1px solid #D8DEE8",
  [theme.breakpoints.down("sm")]: {
    padding: "6px 8px",
    fontSize: "0.75rem",
  },
}));

const SortableHeaderCell = styled(ResponsiveTableCell)(() => ({
  cursor: "pointer",
  userSelect: "none",
  "&:hover": { backgroundColor: "#586280" },
  transition: "background-color 0.2s ease",
}));

const SortIconsContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginLeft: "8px",
}));

const ActionMenu = styled("div")(() => ({
  position: "fixed",
  zIndex: 1300,
  backgroundColor: "#fff",
  border: "1px solid #A5DCF3",
  boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
  borderRadius: "8px",
  width: "160px",
  overflow: "hidden",
  transition: "opacity 0.2s ease-in-out",
}));

// ─── Constants ────────────────────────────────────────────────────────────────

const COLORS = {
  temperature: "#378ADD",
  humidity: "#1D9E75",
  fuel: "#BA7517",
  vibration: "#D4537E",

  smoke: "#7C68EE",
  battery_voltage: "#16A085",
  pressure: "#8E44AD",
  ignition: "#2980B9",
  television: "#20A8E0",
  cctv: "#8E44AD",
  door: "#34495E",
  GPS_TRACKER: "#183059",
};
const ICONS = {
  temperature: "🌡",
  humidity: "💧",
  fuel: "⛽",
  vibration: "📳",
};
Object.assign(ICONS, {
  smoke: "S",
  battery_voltage: "B",
  pressure: "P",
  ignition: "I",
  television: "TV",
  cctv: "C",
  door: "D",
});
const SORT_STATES = { NONE: "none", ASC: "asc", DESC: "desc" };
const STATUS_BADGE = {
  ACTIVE: { bg: "#E1F5EE", color: "#0F6E56" },
  INACTIVE: { bg: "#FEF3C7", color: "#1F242E", textColor: "#C47600" },
  UNASSIGNED: { bg: "#F3F4F6", color: "#6B7280", textColor: "#6B7280" },
};
const WORKING_CONDITION_LABELS = {
  WORKING: "Working",
  NOT_WORKING: "Not Working",
  NEEDS_REPAIR: "Needs Repair",
};
const WORKING_CONDITION_COLORS = {
  WORKING: "#1D9E75",
  NOT_WORKING: "#A32D2D",
  NEEDS_REPAIR: "#B7791F",
};
const ACTIVE_DEVICE_COLUMNS = new Set([
  "DASH_CAMERA",
  "DASHCAMERA",
  "SPEAKER",
  "TELEVISION",
  "CCTV",
]);
const STREAM_DEVICE_COLUMNS = new Set([
  "DASH_CAMERA",
  "DASHCAMERA",
  "TELEVISION",
  "CCTV",
]);
const DETAIL_DEVICE_COLUMNS = new Set(["SPEAKER", "RFID"]);
const SENSOR_READING_COLORS = {
  normal: "#1D9E75",
  warning: "#FF0000",
  alert: "#AF0000",
};
const SENSOR_WARNING_RANGE_RATIO = 0.05;
const STATUS_FILTERS = [
  { id: "ALL", label: "All" },
  { id: "ACTIVE", label: "Active" },
  { id: "INACTIVE", label: "Inactive" },
];
const STOP_STATUS_FILTERS = [
  { id: "ALL", label: "All" },
  { id: "ACTIVE", label: "Active" },
  { id: "UNASSIGNED", label: "Unassigned" },
];
const LEGEND_ITEMS = [
  {
    label: "Active / normal",
    color: SENSOR_READING_COLORS.normal,
    borderColor: SENSOR_READING_COLORS.normal,
  },
  {
    label: "Inactive",
    color: STATUS_BADGE.INACTIVE.textColor,
    borderColor: STATUS_BADGE.INACTIVE.textColor,
  },
  {
    label: "Approaching threshold",
    color: SENSOR_READING_COLORS.warning,
    borderColor: SENSOR_READING_COLORS.warning,
  },
  {
    label: "Outside threshold",
    color: SENSOR_READING_COLORS.alert,
    borderColor: SENSOR_READING_COLORS.alert,
  },
  {
    label: "Increasing",
    color: "#1F242E",
    borderColor: "transparent",
    content: "↑",
    noSwatchBackground: true,
  },
  {
    label: "Decreasing",
    color: "#1F242E",
    borderColor: "transparent",
    content: "↓",
    noSwatchBackground: true,
  },
];
const SENSOR_TYPE_FILTERS = [
  {
    value: "FUEL_LEVEL",
    label: "Fuel Level",
    matchTypes: ["fuel", "fuel_level"],
  },
  { value: "TEMPERATURE", label: "Temperature", matchTypes: ["temperature"] },
  { value: "HUMIDITY", label: "Humidity", matchTypes: ["humidity"] },
  { value: "DOOR", label: "Door", matchTypes: ["door"] },
  { value: "VIBRATION", label: "Vibration", matchTypes: ["vibration"] },
  { value: "SMOKE", label: "Smoke", matchTypes: ["smoke"] },
  { value: "PRESSURE", label: "Pressure", matchTypes: ["pressure"] },
  { value: "IGNITION", label: "Ignition", matchTypes: ["ignition"] },
  { value: "LOAD_WEIGHT", label: "Load Weight", matchTypes: ["load_weight"] },
  { value: "SEAT_BELT", label: "Seat Belt", matchTypes: ["seat_belt"] },
  {
    value: "ENGINE_HEALTH",
    label: "Engine Health",
    matchTypes: ["engine_health"],
  },
  {
    value: "BATTERY_VOLTAGE",
    label: "Battery Voltage",
    matchTypes: ["battery_voltage"],
  },
  { value: "OTHER", label: "Other", matchTypes: ["other"] },
];

const DEVICE_COLUMNS = [
  {
    key: "GPS_TRACKER",
    label: "GPS Trackers",
    matchTypes: ["GPS_TRACKER", "GPS", "gps_tracker", "tracker"],
  },
  { key: "temperature", label: "Temperature", matchTypes: ["temperature"] },
  { key: "humidity", label: "Humidity", matchTypes: ["humidity"] },
  { key: "fuel", label: "Fuel Level", matchTypes: ["fuel", "fuel_level"] },
  { key: "DASH_CAMERA", label: "Dash Cameras", matchTypes: ["DASH_CAMERA"] },
  { key: "vibration", label: "Vibration", matchTypes: ["vibration"] },
  { key: "smoke", label: "Smoke", matchTypes: ["smoke"] },
  { key: "pressure", label: "Pressure", matchTypes: ["pressure"] },
  { key: "ignition", label: "Ignition", matchTypes: ["ignition"] },
  // { key: "RFID_READER", label: "RFID", matchTypes: ["RFID_READER"] },
  { key: "SPEAKER", label: "Speakers", matchTypes: ["SPEAKER"] },
];

const STOP_DEVICE_COLUMNS = [
  { key: "TELEVISION", label: "Television", matchTypes: ["TELEVISION", "TV", "SMART_TV"] },
  { key: "SPEAKER", label: "Speaker", matchTypes: ["SPEAKER"] },
  { key: "CCTV", label: "CCTV", matchTypes: ["CCTV"] },
  {
    key: "DASHCAMERA",
    label: "Dash Camera",
    matchTypes: ["DASH CAMERA", "DASH_CAMERA", "DASHCAMERA"],
  },
  {
    key: "RFID",
    label: "RFID Reader",
    matchTypes: ["RFID READER", "RFID_READER", "RFID"],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pct(v, mn, mx) {
  return Math.max(0, Math.min(100, ((v - mn) / (mx - mn)) * 100));
}
function isLiveSensor(device) {
  return (
    device?.val !== undefined &&
    device?.min !== undefined &&
    device?.max !== undefined
  );
}
function isAlert(d) {
  if (!isLiveSensor(d)) return false;
  if (d.type === "fuel" || d.type === "fuel_level") return d.val < d.min;
  return d.val < d.min || d.val > d.max;
}
function isNearThreshold(d) {
  if (!isLiveSensor(d) || isAlert(d)) return false;

  const range = d.max - d.min;
  if (!Number.isFinite(range) || range <= 0) return false;

  const warningDistance = range * SENSOR_WARNING_RANGE_RATIO;
  if (d.type === "fuel" || d.type === "fuel_level") {
    return d.val - d.min <= warningDistance;
  }

  return (
    d.val - d.min <= warningDistance ||
    d.max - d.val <= warningDistance
  );
}
function getSensorReadingColor(d) {
  if (isAlert(d)) return SENSOR_READING_COLORS.alert;
  if (isNearThreshold(d)) return SENSOR_READING_COLORS.warning;
  return SENSOR_READING_COLORS.normal;
}
function formatCoordinate(value) {
  const num = Number(value);
  return Number.isFinite(num) && num !== 0 ? num.toFixed(6) : null;
}
function getVehicleCoordinateLabel(vehicle, axis) {
  const key = axis === "lat" ? "latitude" : "longitude";
  return formatCoordinate(vehicle?.[key]) || "NA";
}
function pickCoordinate(...values) {
  for (const value of values) {
    const formatted = formatCoordinate(value);
    if (formatted) return { raw: Number(value), formatted };
  }
  return null;
}
function getVehicleCoordinatesDisplay(vehicle = {}, device = {}) {
  const latitude = pickCoordinate(
    vehicle.latitude,
    vehicle.Latitude,
    vehicle.lat,
    device.latitude,
    device.Latitude,
    device.lat,
  );
  const longitude = pickCoordinate(
    vehicle.longitude,
    vehicle.Longitude,
    vehicle.lng,
    vehicle.lon,
    device.longitude,
    device.Longitude,
    device.lng,
    device.lon,
  );
  if (!latitude || !longitude) return null;
  return `${latitude.formatted}, ${longitude.formatted}`;
}
function isGpsTrackerDevice(device) {
  const type = normalizeDeviceType(
    device?.type || device?.sensorType || device?.deviceType,
  );
  return type.includes("gps") || type === "tracker";
}
function normalizeDeviceType(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .toLowerCase();
}
function getLocationDeviceIds(vehicle = {}, gpsDevice = null) {
  const candidates = [
    vehicle.device_id,
    vehicle.deviceId,
    vehicle.gpsDeviceId,
    vehicle.trackingDeviceId,
    gpsDevice?.trackingDeviceId,
    gpsDevice?.id,
  ];

  return [
    ...new Set(
      candidates
        .map((value) => (value == null || value === "" ? null : String(value)))
        .filter(Boolean),
    ),
  ];
}

/** Ensure vehicles with a GPS device_id get a GPS column cell even without a monitor reading. */
function ensureGpsDeviceOnRow(row, sourceVehicle = {}) {
  const existing =
    (row.devices || []).find(isGpsTrackerDevice) ||
    (row.devices || []).find((device) =>
      matchesDeviceColumn(device, DEVICE_COLUMNS[0]),
    );
  if (existing) return existing;

  const deviceId =
    sourceVehicle.device_id ||
    sourceVehicle.deviceId ||
    sourceVehicle.gpsDeviceId ||
    sourceVehicle.trackingDeviceId;
  if (!deviceId) return null;

  const synthetic = {
    id: String(deviceId),
    trackingDeviceId: deviceId,
    type: "gps_tracker",
    status: "",
    workingCondition:
      String(sourceVehicle.status || "").toUpperCase() === "ACTIVE"
        ? "WORKING"
        : "",
    recordedAt: null,
  };
  row.devices = [...(row.devices || []), synthetic];
  return synthetic;
}

function hasLiveSensorOfType(devices = [], type) {
  const wanted = normalizeSensorType(type);
  return devices.some(
    (device) =>
      normalizeSensorType(device?.type || device?.sensorType) === wanted &&
      device?.val !== undefined &&
      device?.val !== null,
  );
}

/** Fill Temperature/Humidity from GPS location payload when no dedicated sensor reading exists. */
function injectGpsEmbeddedSensors(row, locationPayload) {
  const telemetry = extractLiveTelemetry(locationPayload || {});
  const metrics = [
    { key: "temperature", value: telemetry.temperature },
    { key: "humidity", value: telemetry.humidity },
    { key: "vibration", value: telemetry.vibration },
    { key: "smoke", value: telemetry.smoke },
    { key: "voltage", value: telemetry.voltage },
  ];

  metrics.forEach(({ key, value }) => {
    if (value === null || value === undefined) return;
    if (hasLiveSensorOfType(row.devices, key)) return;

    const defaults = DEFAULT_SENSOR_RANGES[key] || {};
    row.devices = [
      ...(row.devices || []),
      {
        id: `gps_embedded_${key}_${row.id}`,
        type: key,
        trackingDeviceId: null,
        val: value,
        min: defaults.min,
        max: defaults.max,
        unit: LIVE_SENSOR_UNITS[key] || "",
        status: "",
        workingCondition: "WORKING",
        recordedAt: locationPayload?.createdAt || locationPayload?.timestamp || null,
        fromGps: true,
      },
    ];
  });
}

function getDeviceColumnType(device) {
  const type = device?.type || device?.sensorType || device?.deviceType;
  return normalizeDeviceType(type);
}
function getDeviceDetailLabel(device) {
  const rawType = device?.type || device?.sensorType || device?.deviceType;
  const normalizedType = normalizeDeviceType(rawType);
  if (normalizedType === "rfid_reader" || normalizedType === "rfid") {
    return "RFID Reader";
  }
  if (normalizedType === "speaker") return "Speaker";
  return rawType ? String(rawType).replace(/_/g, " ") : "Device";
}
function matchesDeviceColumn(device, column) {
  const deviceType = getDeviceColumnType(device);
  return column.matchTypes.some(
    (type) => normalizeDeviceType(type) === deviceType,
  );
}
function findDeviceForColumn(devices = [], column) {
  return devices.find((device) => matchesDeviceColumn(device, column));
}
function getDeviceOperationalStatus(
  device,
  fallbackStatus,
  fallbackColor,
  columnKey,
) {
  const workingCondition = device?.workingCondition;
  if (ACTIVE_DEVICE_COLUMNS.has(columnKey)) {
    if (workingCondition === "WORKING") {
      return {
        label: "Active",
        color: STATUS_BADGE.ACTIVE.color,
      };
    }
    if (workingCondition === "NOT_WORKING") {
      return {
        label: "Inactive",
        color: STATUS_BADGE.INACTIVE.textColor,
      };
    }
  }

  if (workingCondition && WORKING_CONDITION_LABELS[workingCondition]) {
    return {
      label: WORKING_CONDITION_LABELS[workingCondition],
      color: WORKING_CONDITION_COLORS[workingCondition],
    };
  }

  return {
    label: fallbackStatus,
    color: fallbackColor,
  };
}

// ─── SensorCard ───────────────────────────────────────────────────────────────

function SensorCard({ device, prevVal }) {
  const alert = isAlert(device);
  const readingColor = getSensorReadingColor(device);
  const color = COLORS[device.type] || "#888";
  const p = pct(device.val, device.min, device.max);
  const trend =
    prevVal === null
      ? "·"
      : device.val > prevVal
        ? "↑"
        : device.val < prevVal
          ? "↓"
          : "·";
  const trendColor = trend === "·" ? "#aaa" : readingColor;

  return (
    <div
      style={{
        background: "transparent",
        border: "none",
        // borderTop: `2.5px solid ${color}`,
        borderRadius: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        gap: 5,
        alignItems: "center",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "none", alignItems: "center", gap: 5 }}>
          <span style={{ fontSize: 13 }}>{ICONS[device.type] || "📡"}</span>
          <span
            style={{ fontSize: 10, color: "#888", textTransform: "capitalize" }}
          >
            {""}
          </span>
        </div>
        {/* {alert && (
          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <span
              style={{
                fontSize: 16,
                fontWeight: 400,
                background: "transparent",
                color: "#A32D2D",
                padding: 0,
                borderRadius: 99,
                whiteSpace: "nowrap",
              }}
            >
              Alert⚠️ 
            </span>
          </div>
        )} */}
      </div>
      <div
        style={{
          fontSize: alert ? 18 : 16,
          fontWeight: alert ? 700 : 400,
          color: readingColor,
          lineHeight: 1.1,
        }}
      >
        {device.type === "ignition"
          ? device.val >= 0.5
            ? "ON"
            : "OFF"
          : device.val.toFixed(1)}
        <span
          style={{ fontSize: "inherit", fontWeight: "inherit", marginLeft: 2 }}
        >
          {device.unit}
        </span>
        <span style={{ fontSize: "inherit", marginLeft: 4, color: trendColor }}>
          {trend}
        </span>
      </div>
      <div
        style={{
          height: 3,
          background: "#ececea",
          borderRadius: 2,
          overflow: "hidden",
          display: "none",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${p.toFixed(1)}%`,
            background: color,
            borderRadius: 2,
            transition: "width 0.6s ease",
          }}
        />
      </div>
      <div style={{ display: "none", justifyContent: "space-between" }}>
        <span style={{ fontSize: 10, color: "#aaa" }}>{device.id}</span>
      </div>
    </div>
  );
}

function AssignedDeviceCard({
  device,
  vehicle,
  statusLabel,
  statusColor,
  showAsGps = false,
}) {
  const typeLabel = device.type?.replace(/_/g, " ") || "Device";
  const deviceTextColor = statusLabel ? statusColor || "#1D9E75" : "#1a1a18";
  const isGps = showAsGps || isGpsTrackerDevice(device);
  const coordinatesLabel = isGps
    ? getVehicleCoordinatesDisplay(vehicle, device)
    : null;

  return (
    <div
      style={{
        background: "transparent",
        border: "none",
        borderRadius: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        gap: 5,
        alignItems: "center",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <div
        style={{
          fontSize: isGps ? 12 : 16,
          fontWeight: 400,
          color: deviceTextColor,
          lineHeight: 1.3,
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
        }}
        title={
          isGps
            ? coordinatesLabel || "NA"
            : device.model || typeLabel
        }
      >
        {isGps ? coordinatesLabel || "NA" : device.id || typeLabel}
      </div>
    </div>
  );
}

// ─── Sort Icons ───────────────────────────────────────────────────────────────

function SortIcons({ headerKey, sortConfig }) {
  const isCurrentSort = sortConfig.key === headerKey;
  const dir = isCurrentSort ? sortConfig.direction : SORT_STATES.NONE;
  return (
    <SortIconsContainer>
      <span
        style={{
          fontSize: 8,
          lineHeight: 1,
          opacity: dir === SORT_STATES.ASC ? 1 : 0.4,
          color: dir === SORT_STATES.ASC ? "#20A8E0" : "inherit",
        }}
      >
        ▲
      </span>
      <span
        style={{
          fontSize: 8,
          lineHeight: 1,
          opacity: dir === SORT_STATES.DESC ? 1 : 0.4,
          color: dir === SORT_STATES.DESC ? "#20A8E0" : "inherit",
        }}
      >
        ▼
      </span>
    </SortIconsContainer>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const ROW_ACTIONS = [
  { id: "view", label: "View Details", color: "#1F242E", hoverBg: "#20A8E0" },
  //   { id: "delete", label: "Remove",       color: "#e53935", hoverBg: "#e53935" },
];

const MONITOR_REFRESH_MS = 15000;

export default function VehicleSensorDashboard({ mode = "vehicles", onTotalCountChange }) {
  const { hasDownloadPermission } = usePermission();
  const isStopMode = mode === "stops";
  const parsedUserState = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userState") || "null");
    } catch {
      return null;
    }
  }, []);
  const orgName = parsedUserState?.organization?.name;
  const cleanOrgName = (orgName || "RouteEye").replace(/[^a-zA-Z0-9_-]/g, "_");
  const monitorExportFileName = `${cleanOrgName}_${isStopMode ? "Monitor_Stops" : "Monitor_Vehicles"}`;

  const canUserDownload = hasDownloadPermission(
    isStopMode ? "monitor-stop" : "vehicle",
  );
  const allDeviceColumns = isStopMode ? STOP_DEVICE_COLUMNS : DEVICE_COLUMNS;
  const [vehicles, setVehicles] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [prevVals, setPrevVals] = useState({});
  const [filter, setFilter] = useState("ALL");
  const [sensorTypeFilter, setSensorTypeFilter] = useState("");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRow, setActiveRow] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [streamDevice, setStreamDevice] = useState(null);
  const [hoverStream, setHoverStream] = useState(null);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [hoverSensor, setHoverSensor] = useState(null);
  const [selectedGps, setSelectedGps] = useState(null);
  const [hoverGps, setHoverGps] = useState(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [hoverSpeaker, setHoverSpeaker] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: SORT_STATES.NONE,
  });

  const actionMenuRef = useRef(null);
  const actionIconRefs = useRef([]);
  const tableContainerRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchMonitorRows = useCallback(async () => {
    const organizationId = getOrganizationId();
    const readingsResponse = await getDeviceMonitorLatest({ organizationId });
    const readings = readingsResponse?.data || [];

    if (isStopMode) {
      const [stopsResponse, routesResponse] = await Promise.all([
        axios.get(`${config.baseURL}/stop/organization`, {
          params: { organizationId },
          withCredentials: true,
        }),
        axios.get(`${config.baseURL}/route/organization`, {
          params: { organizationId },
          withCredentials: true,
        }),
      ]);
      const organizationStops = Array.isArray(stopsResponse.data)
        ? stopsResponse.data
        : stopsResponse.data?.stops || [];
      const organizationRoutes = Array.isArray(routesResponse.data)
        ? routesResponse.data
        : routesResponse.data?.routes || [];
      const assignedStopIds = new Set();
      organizationRoutes.forEach((route) => {
        (route.routeCoordinates || []).forEach((coord) => {
          if (coord?.stopId) assignedStopIds.add(String(coord.stopId));
        });
        (route.stops || route.assignedStops || []).forEach((stop) => {
          const stopId = stop?.id || stop?.stopId;
          if (stopId) assignedStopIds.add(String(stopId));
        });
      });
      return buildStopMonitorRows(organizationStops, readings, assignedStopIds);
    }

    const vehiclesResponse = await axios.get(
      `${config.baseURL}/vehicle/organization`,
      { params: { organizationId }, withCredentials: true },
    );
    const organizationVehicles = Array.isArray(vehiclesResponse.data)
      ? vehiclesResponse.data
      : vehiclesResponse.data?.vehicles || [];

    const rows = buildVehicleMonitorRows(organizationVehicles, readings);

    // Attach live lat/lng for GPS trackers from latest location API.
    const vehiclesById = new Map(
      organizationVehicles.map((vehicle) => [String(vehicle.id), vehicle]),
    );

    await Promise.all(
      rows.map(async (row) => {
        const sourceVehicle = vehiclesById.get(String(row.id)) || {};
        const gpsDevice = ensureGpsDeviceOnRow(row, sourceVehicle);

        // Keep any coordinates already present on the vehicle record.
        const existingLat = pickCoordinate(
          row.latitude,
          sourceVehicle.latitude,
          sourceVehicle.Latitude,
          sourceVehicle.lat,
          gpsDevice?.latitude,
        );
        const existingLng = pickCoordinate(
          row.longitude,
          sourceVehicle.longitude,
          sourceVehicle.Longitude,
          sourceVehicle.lng,
          gpsDevice?.longitude,
        );
        if (existingLat) row.latitude = existingLat.raw;
        if (existingLng) row.longitude = existingLng.raw;

        const locationDeviceIds = getLocationDeviceIds(sourceVehicle, gpsDevice);
        if (!locationDeviceIds.length) return;

        for (const locationDeviceId of locationDeviceIds) {
          try {
            const { data } = await axios.get(
              `${config.baseURL}/location/latest/${locationDeviceId}`,
              { withCredentials: true },
            );
            if (!data) continue;

            const latitude = pickCoordinate(data.latitude, data.Latitude, data.lat);
            const longitude = pickCoordinate(
              data.longitude,
              data.Longitude,
              data.lng,
              data.lon,
            );
            if (latitude) row.latitude = latitude.raw;
            if (longitude) row.longitude = longitude.raw;

            // Same GPS-embedded temp/humidity source as /Vehicle Live Data.
            injectGpsEmbeddedSensors(row, data);

            if (latitude && longitude) break;
          } catch (error) {
            console.warn(
              `Failed to load GPS location for vehicle ${row.id} / device ${locationDeviceId}:`,
              error,
            );
          }
        }
      }),
    );

    return rows;
  }, [isStopMode]);

  useEffect(() => {
    let isMounted = true;

    const refresh = async (isInitialLoad) => {
      if (isInitialLoad) setLoading(true);
      try {
        const rows = await fetchMonitorRows();
        if (!isMounted) return;

        // Snapshot the readings we are replacing so the table can show
        // whether each sensor moved up or down since the last poll.
        setVehicles((previousRows) => {
          const previousValues = {};
          previousRows.forEach((row) => {
            previousValues[row.id] = {};
            row.devices.forEach((device) => {
              if (!isLiveSensor(device)) return;
              previousValues[row.id][device.id] = device.val;
            });
          });
          setPrevVals(previousValues);
          return rows;
        });
        setLastUpdate(new Date().toLocaleTimeString());
      } catch (error) {
        console.error("Failed to load monitor device readings:", error);
        if (isMounted && isInitialLoad) setVehicles([]);
      } finally {
        if (isMounted && isInitialLoad) setLoading(false);
      }
    };

    refresh(true);
    const intervalId = setInterval(() => refresh(false), MONITOR_REFRESH_MS);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [fetchMonitorRows]);

  // Close menu on outside click/scroll
  useEffect(() => {
    const outside = (e) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(e.target) &&
        !actionIconRefs.current.some((r) => r?.contains(e.target))
      )
        setActiveRow(null);
    };
    const scroll = () => setActiveRow(null);
    document.addEventListener("mousedown", outside);
    window.addEventListener("scroll", scroll);
    window.addEventListener("resize", scroll);
    const tc = tableContainerRef.current;
    if (tc) tc.addEventListener("scroll", scroll);
    return () => {
      document.removeEventListener("mousedown", outside);
      window.removeEventListener("scroll", scroll);
      window.removeEventListener("resize", scroll);
      if (tc) tc.removeEventListener("scroll", scroll);
    };
  }, []);

  // Reposition menu
  useEffect(() => {
    if (activeRow === null || !actionIconRefs.current[activeRow]) return;
    const rect = actionIconRefs.current[activeRow].getBoundingClientRect();
    const menuH = ROW_ACTIONS.length * 44 + 10;
    const menuW = 160;
    const top =
      rect.bottom + 4 + menuH > window.innerHeight
        ? rect.top - menuH - 4
        : rect.bottom + 4;
    const left = Math.max(
      10,
      Math.min(rect.right - menuW, window.innerWidth - menuW - 10),
    );
    setMenuPos({ top, left });
  }, [activeRow]);

  // Sorting
  const handleSort = (key) => {
    if (key !== "vehicle") return;
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: SORT_STATES.ASC };
      if (prev.direction === SORT_STATES.ASC)
        return { key, direction: SORT_STATES.DESC };
      if (prev.direction === SORT_STATES.DESC)
        return { key, direction: SORT_STATES.NONE };
      return { key, direction: SORT_STATES.ASC };
    });
  };

  const filtered = useMemo(() => {
    let list = vehicles.filter((v) => {
      if (filter === "ACTIVE") return v.status === "ACTIVE";
      if (filter === "INACTIVE") return v.status === "INACTIVE";
      if (filter === "UNASSIGNED") return v.status === "UNASSIGNED";
      return true;
    });
    if (sensorTypeFilter) {
      const selectedSensor = SENSOR_TYPE_FILTERS.find(
        (sensor) => sensor.value === sensorTypeFilter,
      );
      const matchTypes = selectedSensor?.matchTypes || [
        sensorTypeFilter.toLowerCase(),
      ];
      list = list.filter((v) =>
        v.devices.some(
          (d) =>
            isLiveSensor(d) &&
            matchTypes.includes(String(d.type).toLowerCase()),
        ),
      );
    }
    if (
      sortConfig.direction !== SORT_STATES.NONE &&
      sortConfig.key === "vehicle"
    ) {
      list = [...list].sort((a, b) => {
        const av = a.name.toLowerCase(),
          bv = b.name.toLowerCase();
        return sortConfig.direction === SORT_STATES.ASC
          ? av.localeCompare(bv)
          : bv.localeCompare(av);
      });
    }
    return list;
  }, [vehicles, filter, sensorTypeFilter, sortConfig]);

  // Hide empty device columns for vehicles. Stop monitor always shows the
  // stop device types so unconnected cells can render as NA.
  const visibleDeviceColumns = useMemo(
    () =>
      isStopMode
        ? allDeviceColumns
        : allDeviceColumns.filter((column) =>
            filtered.some((vehicle) =>
              Boolean(findDeviceForColumn(vehicle.devices, column)),
            ),
          ),
    [allDeviceColumns, filtered, isStopMode],
  );

  const tableHeaders = useMemo(
    () => [
      { key: "vehicle", label: isStopMode ? "Stops" : "Vehicles" },
      ...visibleDeviceColumns,
    ],
    [isStopMode, visibleDeviceColumns],
  );

  useEffect(() => {
    onTotalCountChange?.(filtered.length);
  }, [filtered.length, onTotalCountChange]);

  useEffect(() => {
    setSelectedItemIds([]);
  }, [filter, sensorTypeFilter, mode]);

  const allSelectableIds = useMemo(() => filtered.map((v) => v.id), [filtered]);
  const isAllSelected =
    allSelectableIds.length > 0 &&
    allSelectableIds.every((id) => selectedItemIds.includes(id));
  const isIndeterminate =
    selectedItemIds.length > 0 &&
    !isAllSelected &&
    allSelectableIds.some((id) => selectedItemIds.includes(id));

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItemIds(allSelectableIds);
    } else {
      setSelectedItemIds([]);
    }
  };

  const handleSelectRow = (id, e) => {
    e.stopPropagation();
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectedDownloadRows = useMemo(() => {
    const targetList =
      selectedItemIds.length > 0
        ? filtered.filter((v) => selectedItemIds.includes(v.id))
        : [];
    return targetList.map((v) => {
      const row = {
        vehicle: v.name || "",
      };
      visibleDeviceColumns.forEach((col) => {
        const dev = findDeviceForColumn(v.devices, col);
        if (!dev) {
          row[col.key] = "NA";
          return;
        }

        const isGpsColumn = col.key === "GPS_TRACKER" || isGpsTrackerDevice(dev);
        if (isGpsColumn) {
          const coords = getVehicleCoordinatesDisplay(v, dev);
          row[col.key] = coords || "NA";
          return;
        }

        if (dev.val !== undefined && dev.val !== null) {
          if (dev.type === "ignition") {
            row[col.key] = dev.val >= 0.5 ? "ON" : "OFF";
          } else {
            const num = Number(dev.val);
            const valStr = Number.isInteger(num) ? num.toString() : num.toFixed(1);
            row[col.key] = `${valStr} ${dev.unit || ""}`.trim();
          }
          return;
        }

        const live = extractLiveTelemetry(dev);
        if (live && live.primaryValue != null) {
          row[col.key] = `${live.primaryValue} ${live.unit || ""}`.trim();
          return;
        }

        if (dev.workingCondition) {
          row[col.key] = WORKING_CONDITION_LABELS[dev.workingCondition] || dev.workingCondition;
        } else if (dev.status) {
          row[col.key] = dev.status;
        } else {
          row[col.key] = "NA";
        }
      });
      return row;
    });
  }, [filtered, visibleDeviceColumns, selectedItemIds]);

  const resolveDeviceSelection = (selection) => {
    if (!selection) return { device: null, vehicle: null };
    const vehicle =
      vehicles.find((v) => String(v.id) === String(selection.vehicleId)) ||
      selection.vehicle;
    const device =
      vehicle?.devices?.find(
        (d) => String(d.id) === String(selection.deviceId),
      ) || selection.device;
    return { device, vehicle };
  };

  const handleVehicleMonitor = (vehicle) => {
    if (isStopMode) return;
    if (!vehicle?.id) return;
    navigate("/vehicle/track", { state: vehicle.id });
  };

  const handleDashCameraClick = (device) => {
    if (!device) return;
    setHoverStream(null);
    setStreamDevice(device);
  };

  const handleDashCameraHover = (event, device) => {
    if (!device) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setHoverStream({
      device,
      top: Math.min(rect.bottom + 8, window.innerHeight - 150),
      left: Math.max(12, Math.min(rect.left, window.innerWidth - 252)),
    });
  };

  const handleSensorClick = (device, vehicle) => {
    if (!isLiveSensor(device)) return;
    setHoverSensor(null);
    setSelectedSensor({ device, vehicle, deviceId: device.id, vehicleId: vehicle.id });
  };

  const handleGpsClick = (device, vehicle) => {
    if (!device) return;
    setHoverGps(null);
    setSelectedGps({ device, vehicle, deviceId: device.id, vehicleId: vehicle.id });
  };

  const handleGpsHover = (event, device, vehicle) => {
    if (!device) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setHoverGps({
      device,
      vehicle,
      deviceId: device.id,
      vehicleId: vehicle.id,
      top: Math.min(rect.bottom + 8, window.innerHeight - 176),
      left: Math.max(12, Math.min(rect.left, window.innerWidth - 292)),
    });
  };

  const handleSpeakerClick = (device, vehicle) => {
    if (!device) return;
    setHoverSpeaker(null);
    setSelectedSpeaker({ device, vehicle });
  };

  const handleSpeakerHover = (event, device, vehicle) => {
    if (!device) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 240;
    const popupHeight = 150;
    setHoverSpeaker({
      device,
      vehicle,
      top: Math.max(12, Math.min(rect.bottom + 8, window.innerHeight - popupHeight - 12)),
      left: Math.max(12, Math.min(rect.left, window.innerWidth - popupWidth - 12)),
    });
  };

  const handleSensorHover = (event, device, vehicle) => {
    if (!isLiveSensor(device)) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setHoverSensor({
      device,
      vehicle,
      deviceId: device.id,
      vehicleId: vehicle.id,
      top: Math.min(rect.bottom + 8, window.innerHeight - 188),
      left: Math.max(12, Math.min(rect.left, window.innerWidth - 292)),
    });
  };

  return (
    <div
      style={{
        padding: "20px 24px",
        fontFamily: "system-ui, sans-serif",
        fontSize: 13,
        color: "#1a1a18",
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
          flexWrap: "nowrap",
          overflowX: "auto",
          gap: 12,
          whiteSpace: "nowrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#1D9E75",
              display: "inline-block",
              animation: "pulse 1.4s infinite",
            }}
          />
          <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.25}}`}</style>
          <span style={{ fontWeight: 500, color: "#555" }}>{t("Live")}</span>
          {lastUpdate && (
            <span style={{ fontSize: 11, color: "#aaa" }}>
              {t("Updated")} {lastUpdate}
            </span>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "nowrap",
              backgroundColor: "rgba(255,255,255,0.95)",
              borderRadius: 8,
              padding: "3px 6px",
              border: "1px solid #E5E7EB",
            }}
          >
            {LEGEND_ITEMS.map((item) => (
              <span
                key={item.label}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 11,
                  color: "#666",
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  style={{
                    width: 24,
                    height: 12,
                    borderRadius: item.noSwatchBackground ? 0 : 6,
                    background: item.noSwatchBackground
                      ? "transparent"
                      : item.color,
                    border: item.noSwatchBackground
                      ? "none"
                      : `1.5px solid ${item.borderColor || item.color}`,
                    color: item.noSwatchBackground ? item.color || "#1F242E" : "#FFFFFF",
                    display: "inline-block",
                    fontSize: item.noSwatchBackground ? 14 : 10,
                    fontWeight: 700,
                    lineHeight: "12px",
                    textAlign: "center",
                  }}
                >
                  {item.content || ""}
                </span>
                {t(item.label)}
              </span>
            ))}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            flexWrap: "nowrap",
            flexShrink: 0,
          }}
        >
          {(isStopMode ? STOP_STATUS_FILTERS : STATUS_FILTERS).map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                fontSize: 11,
                padding: "4px 11px",
                borderRadius: 99,
                border: "0.5px solid",
                borderColor: filter === f.id ? "transparent" : "#d5d3cc",
                background: filter === f.id ? "#E6F1FB" : "transparent",
                color: filter === f.id ? "#185FA5" : "#666",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {t(f.label)}
            </button>
          ))}
          <Select
            size="small"
            displayEmpty
            value={sensorTypeFilter}
            onChange={(e) => setSensorTypeFilter(e.target.value)}
            sx={{
              minWidth: 150,
              height: 30,
              fontSize: 11,
              borderRadius: "16px",
              color: sensorTypeFilter ? "#185FA5" : "#666",
              background: sensorTypeFilter ? "#E6F1FB" : "transparent",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: sensorTypeFilter ? "transparent" : "#d5d3cc",
              },
              "& .MuiSelect-select": {
                py: "4px",
                pl: "11px",
              },
            }}
          >
            <MenuItem value="">{t("Sensor Type")}</MenuItem>
            {SENSOR_TYPE_FILTERS.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {t(type.label)}
              </MenuItem>
            ))}
          </Select>
          {canUserDownload && selectedItemIds.length > 0 && (
            <div
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#0284c7",
                background: "#e0f2fe",
                padding: "3px 8px",
                borderRadius: "12px",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {selectedItemIds.length} {t("selected")}
            </div>
          )}
          {canUserDownload && (
            <DownloadDataButton
              headers={tableHeaders.map((h) => ({ label: t(h.label), key: h.key }))}
              data={selectedDownloadRows}
              filename={monitorExportFileName}
              disabled={selectedItemIds.length === 0}
              tooltipTitle={
                selectedItemIds.length === 0
                  ? t("Please select records to download")
                  : `${t("Download")} (${selectedItemIds.length})`
              }
            />
          )}
        </div>
      </div>

      {/* Table */}
      <ResponsiveTableContainer ref={tableContainerRef}>
        <Table
          sx={{
            tableLayout: "fixed",
            width: "100%",
            minWidth: isSmallScreen ? "100%" : `${Math.max(tableHeaders.length * 150, 900)}px`,
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: "#647490" }}>
              {canUserDownload && (
                <ResponsiveTableCell
                  sx={{
                    backgroundColor: "#647490",
                    width: "48px",
                    minWidth: "48px",
                    maxWidth: "48px",
                    padding: "4px 8px",
                    textAlign: "center",
                    borderBottom: "none",
                    borderTopLeftRadius: "10px",
                  }}
                >
                  <Checkbox
                    size="small"
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    sx={{
                      color: "#FFFFFF",
                      "&.Mui-checked": { color: "#FFFFFF" },
                      "&.MuiCheckbox-indeterminate": { color: "#FFFFFF" },
                      padding: "2px",
                    }}
                  />
                </ResponsiveTableCell>
              )}
              {tableHeaders.map((h, idx) => {
                const isSortable = h.key === "vehicle";
                const Cell = isSortable
                  ? SortableHeaderCell
                  : ResponsiveTableCell;
                return (
                  <Cell
                    key={h.key}
                    onClick={() => handleSort(h.key)}
                    sx={{
                      backgroundColor: "#647490",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "20px",
                      borderBottom: "none",
                      width: `${100 / tableHeaders.length}%`,
                      borderTopLeftRadius: idx === 0 && !canUserDownload ? "10px" : 0,
                      borderTopRightRadius:
                        idx === tableHeaders.length - 1 ? "10px" : 0,
                    }}
                  >
                    {loading ? (
                      <Skeleton
                        width="80%"
                        sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
                      />
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontWeight: 600,
                        }}
                      >
                        {t(h.label)}
                        {isSortable && (
                          <SortIcons
                            headerKey={h.key}
                            sortConfig={sortConfig}
                          />
                        )}
                      </div>
                    )}
                  </Cell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array(5)
                .fill(0)
                .map((_, i) => (
                  <TableRow
                    key={`sk-${i}`}
                    sx={{
                      height: 50,
                      "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    {canUserDownload && (
                      <ResponsiveTableCell
                        sx={{
                          width: "48px",
                          minWidth: "48px",
                          maxWidth: "48px",
                          padding: "4px 8px",
                          textAlign: "center",
                        }}
                      >
                        <Skeleton variant="rectangular" width={18} height={18} sx={{ margin: "auto" }} />
                      </ResponsiveTableCell>
                    )}
                    {tableHeaders.map((header) => (
                      <ResponsiveTableCell key={header.key}>
                        <Skeleton variant="text" width="70%" />
                      </ResponsiveTableCell>
                    ))}
                  </TableRow>
                ))
            ) : filtered.length > 0 ? (
              filtered.map((v, rowIndex) => {
                const badge = STATUS_BADGE[v.status] || STATUS_BADGE.INACTIVE;
                const isActive = activeRow === rowIndex;
                const rawVehicleStatus = v.status || "INACTIVE";
                const vehicleStatus =
                  rawVehicleStatus.charAt(0) +
                  rawVehicleStatus.slice(1).toLowerCase();
                const vehicleStatusColor =
                  (STATUS_BADGE[rawVehicleStatus] || STATUS_BADGE.INACTIVE)
                    .textColor ||
                  (STATUS_BADGE[rawVehicleStatus] || STATUS_BADGE.INACTIVE)
                    .color;
                return (
                  <TableRow
                    key={v.id}
                    sx={{
                      height: 50,
                      "&:nth-of-type(odd)": {
                        backgroundColor: isActive ? "#e3f2fd" : "#f5f5f5",
                      },
                      "&:nth-of-type(even)": {
                        backgroundColor: isActive ? "#e3f2fd" : "#ffffff",
                      },
                      ...(isActive && {
                        backgroundColor: "#e3f2fd !important",
                      }),
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    {canUserDownload && (
                      <ResponsiveTableCell
                        sx={{
                          width: "48px",
                          minWidth: "48px",
                          maxWidth: "48px",
                          padding: "4px 8px",
                          textAlign: "center",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedItemIds.includes(v.id)}
                          onChange={(e) => handleSelectRow(v.id, e)}
                          sx={{
                            padding: "2px",
                            color: "#647490",
                            "&.Mui-checked": { color: "#185FA5" },
                          }}
                        />
                      </ResponsiveTableCell>
                    )}
                    {/* Vehicle cell */}
                    <ResponsiveTableCell
                      onClick={() => handleVehicleMonitor(v)}
                      sx={{
                        verticalAlign: "middle",
                        cursor: isStopMode ? "default" : "pointer",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Tooltip
                        title={
                          isStopMode
                            ? `${v.model || "Smart Stop"}${v.number ? ` · ${v.number}` : ""}`
                            : `${v.make} ${v.model} · ${v.year}`
                        }
                      >
                        <div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              marginBottom: 2,
                            }}
                          >
                            <span
                              style={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                flexShrink: 0,
                                display: "none",
                              }}
                            />
                            <span
                              style={{
                                fontWeight: 700,
                                fontSize: 16,
                                color: vehicleStatusColor,
                              }}
                            >
                              {v.name}
                            </span>
                            <span
                              style={{
                                fontSize: 16,
                                fontWeight: 400,
                                background: badge.bg,
                                color: badge.color,
                                padding: "1px 7px",
                                borderRadius: 99,
                                display: "none",
                              }}
                            >
                              {t(
                                rawVehicleStatus.charAt(0) +
                                  rawVehicleStatus.slice(1).toLowerCase()
                              )}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 16,
                              color: "#888",
                              paddingLeft: 13,
                              display: "none",
                            }}
                          >
                            {v.number}
                          </div>
                          <div
                            style={{
                              fontSize: 16,
                              color: "#aaa",
                              paddingLeft: 13,
                              marginTop: 1,
                              display: "none",
                            }}
                          >
                            {v.make} {v.model}
                          </div>
                        </div>
                      </Tooltip>
                    </ResponsiveTableCell>

                    {visibleDeviceColumns.map((column) => {
                      const device = findDeviceForColumn(v.devices, column);
                      const isStreamDeviceColumn = STREAM_DEVICE_COLUMNS.has(
                        column.key,
                      );
                      const isDetailDeviceColumn = DETAIL_DEVICE_COLUMNS.has(
                        column.key,
                      );
                      const isGpsColumn = column.key === "GPS_TRACKER";
                      const assignedDeviceStatus = isGpsColumn
                          ? {
                              label: vehicleStatus,
                              color: vehicleStatusColor,
                            }
                          : getDeviceOperationalStatus(
                              device,
                              vehicleStatus,
                              vehicleStatusColor,
                              column.key,
                            );

                      return (
                        <ResponsiveTableCell
                          key={column.key}
                          sx={{
                            verticalAlign: "middle",
                          cursor:
                              (isStreamDeviceColumn && device) ||
                              isLiveSensor(device) ||
                              (isGpsColumn && device) ||
                              (isDetailDeviceColumn && device)
                                ? "pointer"
                                : "default",
                          }}
                          onClick={isStreamDeviceColumn && device
                              ? () => handleDashCameraClick(device)
                              : isGpsColumn && device
                              ? () => handleGpsClick(device, v)
                              : isDetailDeviceColumn && device
                                ? () => handleSpeakerClick(device, v)
                              : isLiveSensor(device)
                                ? () => handleSensorClick(device, v)
                              : undefined
                          }
                          onMouseEnter={
                            isStreamDeviceColumn && device
                              ? (event) => handleDashCameraHover(event, device)
                              : isGpsColumn && device
                                ? (event) => handleGpsHover(event, device, v)
                              : isDetailDeviceColumn && device
                                ? (event) =>
                                    handleSpeakerHover(event, device, v)
                              : isLiveSensor(device)
                                ? (event) => handleSensorHover(event, device, v)
                              : undefined
                          }
                          onMouseLeave={
                            isStreamDeviceColumn && device
                              ? () => setHoverStream(null)
                              : isGpsColumn && device
                                ? () => setHoverGps(null)
                              : isDetailDeviceColumn && device
                                ? () => setHoverSpeaker(null)
                              : isLiveSensor(device)
                                ? () => setHoverSensor(null)
                              : undefined
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              minHeight: 42,
                            }}
                          >
                            {device ? (
                              isLiveSensor(device) && !isGpsColumn ? (
                                <SensorCard
                                  device={device}
                                  prevVal={prevVals[v.id]?.[device.id] ?? null}
                                />
                              ) : (
                                <AssignedDeviceCard
                                  device={device}
                                  vehicle={v}
                                  showAsGps={isGpsColumn}
                                  statusLabel={assignedDeviceStatus.label}
                                  statusColor={assignedDeviceStatus.color}
                                />
                              )
                            ) : (
                              <span
                                style={{
                                  color: "#9CA3AF",
                                  fontSize: 16,
                                  fontWeight: 400,
                                }}
                              >
                                {t("NA")}
                              </span>
                            )}
                          </div>
                        </ResponsiveTableCell>
                      );
                    })}

                    {false && (
                      <ResponsiveTableCell sx={{ display: "none" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          {v.devices.length === 0 ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                color: "#aaa",
                                fontSize: 11,
                              }}
                            >
                              <span>📡</span>
                              <span>{t("No devices assigned")}</span>
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8,
                              }}
                            >
                              {v.devices.map((d) =>
                                isLiveSensor(d) ? (
                                  <SensorCard
                                    key={d.id}
                                    device={d}
                                    prevVal={prevVals[v.id]?.[d.id] ?? null}
                                  />
                                ) : (
                                  <AssignedDeviceCard key={d.id} device={d} />
                                ),
                              )}
                            </div>
                          )}
                          {/* Action icon (⋮) */}
                          <div
                            ref={(el) =>
                              (actionIconRefs.current[rowIndex] = el)
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveRow(isActive ? null : rowIndex);
                            }}
                            style={{
                              cursor: "pointer",
                              padding: "4px 8px",
                              flexShrink: 0,
                              color: "#647490",
                              fontSize: 20,
                              fontWeight: 700,
                              userSelect: "none",
                            }}
                            role="button"
                            aria-label={t("actions")}
                          >
                            ⋮
                          </div>
                        </div>
                      </ResponsiveTableCell>
                    )}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <ResponsiveTableCell
                  colSpan={canUserDownload ? tableHeaders.length + 1 : tableHeaders.length}
                  sx={{ textAlign: "center", height: 100 }}
                >
                  {t("No data available")}
                </ResponsiveTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Action Menu */}
        {activeRow !== null && (
          <ActionMenu
            ref={actionMenuRef}
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            {/* Arrow pointer */}
            <div
              style={{
                position: "absolute",
                top: -5,
                right: 10,
                width: 0,
                height: 0,
                borderLeft: "5px solid transparent",
                borderRight: "5px solid transparent",
                borderBottom: "5px solid #A5DCF3",
              }}
            />
            {ROW_ACTIONS.map((action) => (
              <div
                key={action.id}
                onClick={() => {
                  const selectedVehicle = filtered[activeRow];
                  setActiveRow(null);
                  if (action.id === "view") {
                    handleVehicleMonitor(selectedVehicle);
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = action.hoverBg;
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "";
                  e.currentTarget.style.color = action.color;
                }}
                style={{
                  padding: "10px 15px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 14,
                  color: action.color,
                  whiteSpace: "nowrap",
                  transition: "background-color 0.2s",
                }}
                role="menuitem"
                tabIndex={0}
              >
                {t(action.label)}
              </div>
            ))}
          </ActionMenu>
        )}
      </ResponsiveTableContainer>

      {selectedSpeaker && (
        <div
          onClick={() => setSelectedSpeaker(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1500,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(420px, 92vw)",
              background: "#FFFFFF",
              borderRadius: 8,
              border: "1px solid #D8DEE8",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              overflow: "hidden",
            }}
          >
            {(() => {
              const { device } = selectedSpeaker;
              const detailLabel = getDeviceDetailLabel(device);
              const conditionLabel =
                WORKING_CONDITION_LABELS[device.workingCondition] || "-";
              const conditionColor =
                WORKING_CONDITION_COLORS[device.workingCondition] ||
                "#1F242E";

              return (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      padding: "10px 12px",
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedSpeaker(null)}
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        border: "1px solid #CBD5E1",
                        background: "#F8FAFC",
                        color: "#1F242E",
                        cursor: "pointer",
                        fontSize: 18,
                        lineHeight: "26px",
                      }}
                      aria-label={t("Close device details", "Close device details")}
                    >
                      x
                    </button>
                  </div>

                  <div style={{ padding: 18 }}>
                    <div
                      style={{
                        display: "grid",
                        gap: 8,
                        fontSize: 14,
                      }}
                    >
                      {[
                        [detailLabel, device.id || "-"],
                        ["Model", device.model || "-"],
                        ["Working Condition", conditionLabel],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 16,
                            padding: "8px 0",
                            borderTop: "1px solid #EEF2F7",
                          }}
                        >
                          <span style={{ color: "#647490" }}>{t(label)}</span>
                          <span
                            style={{
                              color:
                                label === "Working Condition"
                                  ? conditionColor
                                  : "#1F242E",
                              fontWeight: 400,
                              textAlign: "right",
                            }}
                          >
                            {label === "Working Condition" ? t(value) : value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {selectedGps && (
        <div
          onClick={() => setSelectedGps(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1500,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(420px, 92vw)",
              background: "#FFFFFF",
              borderRadius: 8,
              border: "1px solid #D8DEE8",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              overflow: "hidden",
            }}
          >
            {(() => {
              const { device, vehicle } = resolveDeviceSelection(selectedGps);
              if (!device || !vehicle) return null;
              const latitude = getVehicleCoordinateLabel(vehicle, "lat");
              const longitude = getVehicleCoordinateLabel(vehicle, "lng");
              const gpsStatusColor =
                vehicle?.status === "ACTIVE"
                  ? STATUS_BADGE.ACTIVE.color
                  : STATUS_BADGE.INACTIVE.textColor;

              return (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      padding: "10px 12px",
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedGps(null)}
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        border: "1px solid #CBD5E1",
                        background: "#F8FAFC",
                        color: "#1F242E",
                        cursor: "pointer",
                        fontSize: 18,
                        lineHeight: "26px",
                      }}
                      aria-label={t("Close GPS details")}
                    >
                      x
                    </button>
                  </div>

                  <div style={{ padding: 18 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 10,
                      }}
                    >
                      {[
                        ["Latitude", latitude],
                        ["Longitude", longitude],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          style={{
                            border: "1px solid #E5E7EB",
                            borderRadius: 8,
                            padding: "12px 8px",
                            background: "#F8FAFC",
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 12,
                              color: "#647490",
                              marginBottom: 5,
                            }}
                          >
                            {t(label)}
                          </div>
                          <div
                            style={{
                              fontSize: 18,
                              fontWeight: 700,
                              color: "#1F242E",
                            }}
                          >
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      style={{
                        marginTop: 14,
                        display: "grid",
                        gap: 8,
                        fontSize: 14,
                      }}
                    >
                      {[
                        ["Vehicle", vehicle?.name || "-"],
                        ["GPS Tracker", device.id || "-"],
                        [
                          "Status",
                          vehicle?.status === "ACTIVE" ? "Active" : "Inactive",
                        ],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 16,
                            padding: "8px 0",
                            borderTop: "1px solid #EEF2F7",
                          }}
                        >
                          <span style={{ color: "#647490" }}>{t(label)}</span>
                          <span
                            style={{
                              color: label === "Status" ? gpsStatusColor : "#1F242E",
                              fontWeight: 600,
                              textAlign: "right",
                            }}
                          >
                            {label === "Status" ? t(value) : value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {selectedSensor && (
        <div
          onClick={() => setSelectedSensor(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1500,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(460px, 92vw)",
              background: "#FFFFFF",
              borderRadius: 8,
              border: "1px solid #D8DEE8",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              overflow: "hidden",
            }}
          >
            {(() => {
              const { device } = resolveDeviceSelection(selectedSensor);
              if (!device) return null;
              const readingColor = getSensorReadingColor(device);
              const sensorIsAlert = isAlert(device);

              return (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      padding: "10px 12px",
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedSensor(null)}
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        border: "1px solid #CBD5E1",
                        background: "#F8FAFC",
                        color: "#1F242E",
                        cursor: "pointer",
                        fontSize: 18,
                        lineHeight: "26px",
                      }}
                      aria-label={t("Close sensor details")}
                    >
                      x
                    </button>
                  </div>

                  <div style={{ padding: 18 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 10,
                      }}
                    >
                      {[
                        ["Current", `${device.val.toFixed(1)} ${device.unit || ""}`.trim(), readingColor],
                        ["Min", `${device.min} ${device.unit || ""}`.trim(), "#647490"],
                        ["Max", `${device.max} ${device.unit || ""}`.trim(), "#647490"],
                      ].map(([label, value, color]) => (
                        <div
                          key={label}
                          style={{
                            border: "1px solid #E5E7EB",
                            borderRadius: 8,
                            padding: "10px 8px",
                            background: "#F8FAFC",
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 12,
                              color: "#647490",
                              marginBottom: 5,
                            }}
                          >
                            {t(label)}
                          </div>
                          <div
                            style={{
                              fontSize: label === "Current" ? 20 : 16,
                              fontWeight:
                                sensorIsAlert && label === "Current"
                                  ? 700
                                  : 400,
                              color,
                            }}
                          >
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      style={{
                        marginTop: 14,
                        display: "grid",
                        gap: 8,
                        fontSize: 14,
                      }}
                    >
                      {[
                        ["Sensor ID", device.id || "-"],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 16,
                            padding: "8px 0",
                            borderTop: "1px solid #EEF2F7",
                          }}
                        >
                          <span style={{ color: "#647490" }}>{t(label)}</span>
                          <span style={{ color: "#1F242E", fontWeight: 600 }}>
                            {value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {hoverSpeaker && !selectedSpeaker && (
        <div
          style={{
            position: "fixed",
            top: hoverSpeaker.top,
            left: hoverSpeaker.left,
            zIndex: 1400,
            width: 240,
            maxWidth: "calc(100vw - 24px)",
            boxSizing: "border-box",
            background: "#FFFFFF",
            borderRadius: 8,
            border: "1px solid #D8DEE8",
            boxShadow: "0 12px 30px rgba(0,0,0,0.22)",
            padding: 12,
            maxHeight: "calc(100vh - 24px)",
            overflowY: "auto",
            pointerEvents: "none",
          }}
        >
          {(() => {
            const { device } = hoverSpeaker;
            const detailLabel = getDeviceDetailLabel(device);
            const conditionLabel =
              WORKING_CONDITION_LABELS[device.workingCondition] || "-";
            const conditionColor =
              WORKING_CONDITION_COLORS[device.workingCondition] || "#1F242E";

            return (
              <>
                <div
                  style={{
                    display: "grid",
                    gap: 6,
                    fontSize: 13,
                  }}
                >
                  {[
                    [detailLabel, device.id || "-"],
                    ["Model", device.model || "-"],
                    ["Working Condition", conditionLabel],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        paddingTop: 6,
                        borderTop: "1px solid #EEF2F7",
                      }}
                    >
                      <span style={{ color: "#647490" }}>{t(label)}</span>
                      <span
                        style={{
                          color:
                            label === "Working Condition"
                              ? conditionColor
                              : "#1F242E",
                          fontWeight: 400,
                          textAlign: "right",
                        }}
                      >
                        {label === "Working Condition" ? t(value) : value}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {hoverGps && !selectedGps && (
        <div
          style={{
            position: "fixed",
            top: hoverGps.top,
            left: hoverGps.left,
            zIndex: 1400,
            width: 280,
            background: "#FFFFFF",
            borderRadius: 8,
            border: "1px solid #D8DEE8",
            boxShadow: "0 12px 30px rgba(0,0,0,0.22)",
            padding: 12,
            pointerEvents: "none",
          }}
        >
          {(() => {
            const { device, vehicle } = resolveDeviceSelection(hoverGps);
            if (!device || !vehicle) return null;
            const latitude = getVehicleCoordinateLabel(vehicle, "lat");
            const longitude = getVehicleCoordinateLabel(vehicle, "lng");
            const gpsStatusColor =
              vehicle?.status === "ACTIVE"
                ? STATUS_BADGE.ACTIVE.color
                : STATUS_BADGE.INACTIVE.textColor;

            return (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                  }}
                >
                  {[
                    ["Latitude", latitude],
                    ["Longitude", longitude],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        border: "1px solid #E5E7EB",
                        borderRadius: 8,
                        padding: "8px 6px",
                        background: "#F8FAFC",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: "#647490",
                          marginBottom: 4,
                        }}
                      >
                        {t(label)}
                      </div>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: "#1F242E",
                        }}
                      >
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    marginTop: 10,
                    display: "grid",
                    gap: 6,
                    fontSize: 13,
                  }}
                >
                  {[
                    ["Vehicle", vehicle?.name || "-"],
                    ["GPS Tracker", device.id || "-"],
                    [
                      "Status",
                      vehicle?.status === "ACTIVE" ? "Active" : "Inactive",
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        paddingTop: 6,
                        borderTop: "1px solid #EEF2F7",
                      }}
                    >
                      <span style={{ color: "#647490" }}>{t(label)}</span>
                      <span
                        style={{
                          color:
                            label === "Status" ? gpsStatusColor : "#1F242E",
                          fontWeight: 600,
                          textAlign: "right",
                        }}
                      >
                        {label === "Status" ? t(value) : value}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {hoverSensor && !selectedSensor && (
        <div
          style={{
            position: "fixed",
            top: hoverSensor.top,
            left: hoverSensor.left,
            zIndex: 1400,
            width: 280,
            background: "#FFFFFF",
            borderRadius: 8,
            border: "1px solid #D8DEE8",
            boxShadow: "0 12px 30px rgba(0,0,0,0.22)",
            padding: 12,
            pointerEvents: "none",
          }}
        >
          {(() => {
            const { device } = resolveDeviceSelection(hoverSensor);
            if (!device) return null;
            const readingColor = getSensorReadingColor(device);
            const sensorIsAlert = isAlert(device);
            return (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 8,
                  }}
                >
                  {[
                    [
                      "Current",
                      `${device.val.toFixed(1)} ${device.unit || ""}`.trim(),
                      readingColor,
                    ],
                    ["Min", `${device.min} ${device.unit || ""}`.trim(), "#647490"],
                    ["Max", `${device.max} ${device.unit || ""}`.trim(), "#647490"],
                  ].map(([label, value, color]) => (
                    <div
                      key={label}
                      style={{
                        border: "1px solid #E5E7EB",
                        borderRadius: 8,
                        padding: "8px 6px",
                        background: "#F8FAFC",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: "#647490",
                          marginBottom: 4,
                        }}
                      >
                        {t(label)}
                      </div>
                      <div
                        style={{
                          fontSize: label === "Current" ? 17 : 14,
                          fontWeight:
                            sensorIsAlert && label === "Current" ? 700 : 400,
                          color,
                        }}
                      >
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    marginTop: 10,
                    display: "grid",
                    gap: 6,
                    fontSize: 13,
                  }}
                >
                  {[
                    ["Sensor ID", device.id || "-"],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        paddingTop: 6,
                        borderTop: "1px solid #EEF2F7",
                      }}
                    >
                      <span style={{ color: "#647490" }}>{t(label)}</span>
                      <span style={{ color: "#1F242E", fontWeight: 600 }}>
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {hoverStream && !streamDevice && (
        <div
          style={{
            position: "fixed",
            top: hoverStream.top,
            left: hoverStream.left,
            zIndex: 1400,
            width: 240,
            aspectRatio: "16 / 9",
            background:
              "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.08) 0 1px, transparent 1px), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.06) 0 1px, transparent 1px), radial-gradient(circle at 40% 80%, rgba(255,255,255,0.04) 0 1px, transparent 1px), #050505",
            backgroundSize: "9px 9px, 13px 13px, 17px 17px, auto",
            borderRadius: 8,
            border: "1px solid #2A2A2A",
            boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#E5E7EB",
            fontSize: 18,
            fontWeight: 600,
            pointerEvents: "none",
          }}
        >
          {t("No Stream")}
        </div>
      )}

      {streamDevice && (
        <div
          onClick={() => setStreamDevice(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1500,
            background: "rgba(0, 0, 0, 0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(720px, 92vw)",
              aspectRatio: "16 / 9",
              background:
                "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.08) 0 1px, transparent 1px), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.06) 0 1px, transparent 1px), radial-gradient(circle at 40% 80%, rgba(255,255,255,0.04) 0 1px, transparent 1px), #050505",
              backgroundSize: "9px 9px, 13px 13px, 17px 17px, auto",
              borderRadius: 8,
              border: "1px solid #2A2A2A",
              boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#E5E7EB",
              fontSize: 28,
              fontWeight: 600,
            }}
          >
            <button
              type="button"
              onClick={() => setStreamDevice(null)}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "1px solid #4B5563",
                background: "#111827",
                color: "#F9FAFB",
                cursor: "pointer",
                fontSize: 18,
                lineHeight: "28px",
              }}
              aria-label={t("Close stream preview")}
            >
              x
            </button>
            {t("No Stream")}
          </div>
        </div>
      )}
    </div>
  );
}
